/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import NewUserPaymentView from 'components/FrictionlessCheckout/PaymentReleaseFour/NewUserPaymentView';
import processEvent from 'analytics/processEvent';
import OrderUtils from 'utils/Order';
import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import { KlarnaExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna';
import { AfterPayExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import { VenmoExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo';
import PazeExpandedView from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/PazeExpandedView';
import PayPalExpandedView from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paypal/PayPalExpandedView';
import Debounce from 'utils/Debounce';
import checkoutUtils from 'utils/Checkout';
import ApplePay from 'services/ApplePay';
import {
    Box, Icon, Divider, Text, Button
} from 'components/ui';
import CreditCardArea from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard/CreditCardArea';
import UseAGiftCard from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard';
import AppliedGiftCards from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/AppliedGiftCards';
import AddCreditCardButton from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard/AddCreditCardButton';
import CreditCardsList from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList';
import CreditCardRow from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList/CreditCardRow';
import SavedAlternatePaymentRadio from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/SavedAlternatePaymentRadio';
import creditCardUtils from 'utils/CreditCard';
import userUtils from 'utils/User';
import agentAwareUtils from 'utils/AgentAware';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import deepExtend from 'utils/deepExtend';
import checkoutBindings from 'analytics/bindings/pages/checkout/checkoutBindings';
import ErrorsUtils from 'utils/Errors';
import ErrorMsg from 'components/ErrorMsg';
import AccountCredit from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AccountCredit';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import { screenReaderOnlyStyle } from 'style/config';
import { SECTION_NAMES, ALTERNATE_PAYMENT_TYPES, MAX_CARDS_LIMIT } from 'constants/frictionlessCheckout';
import { createKlarnaWidgetLoader } from 'components/FrictionlessCheckout/PaymentReleaseFour/utils/AlternatePaymentWidgetHelpers';
import Venmo from 'utils/Venmo';
import pazeUtils from 'utils/Paze';
import localeUtils from 'utils/LanguageLocale';
import PayPal from 'utils/PayPal';
import PayPalCheckout from 'utils/PayPalCheckout';
import Location from 'utils/Location';
import { refreshCheckout } from 'services/Checkout';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import Empty from 'constants/empty';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import checkoutApi from 'services/api/checkout';
import store from 'store/Store';
import PayPalExpressBindings from 'analytics/bindingMethods/components/rwdBasket/PayPalExpressBindings';

const {
    EVENT_NAMES: { CHECKOUT },
    CONTEXT,
    ACTION_INFO
} = anaConsts;

const { PAYMENT_FLAGS } = OrderUtils;

class PaymentMethodList extends BaseClass {
    hasTrackedButtonState = false;

    hasTrackedUseThisCardState = false;

    state = {
        openCVCModal: false,
        isPayWithPayPal: this.props.isPayPalSelected,
        shouldShowPaze: false,
        openCreditCardForm: this.props.openCreditCardForm,
        isApplePayEnabled: false,
        isApplePayFlow: false,
        isPayWithApplePay: false,
        selectedCreditCardId: null,
        isAfterPayExpanded: false,
        afterpayApiData: null,
        isAfterPayLoading: false,
        afterpayError: null,
        isKlarnaExpanded: false,
        klarnaApiData: null,
        isKlarnaLoading: false,
        klarnaError: null,
        isPazeExpanded: false,
        useMyShippingAddress: true,
        isVenmoExpanded: false,
        isPayPalExpanded: false,
        isGiftCardFormOpen: false,
        selectedAlternatePaymentType: null,
        useBraintreePayPalCheckout: false
    };

    getSelectedCardTypeForAnalytics = () => {
        const { paymentOptions } = this.props;
        const { selectedCreditCardId } = this.state;

        const selectedCard = paymentOptions?.creditCards?.find(card => card.creditCardId === selectedCreditCardId);

        return selectedCard?.cardType;
    };

    /* eslint-disable-next-line complexity */
    componentDidMount() {
        const {
            creditCardPaymentGroup,
            isPayPalSelected,
            paymentName,
            hasPendingCreditCard,
            alternateMethodSelected,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder
        } = this.props;

        // Compute PayPal Braintree Checkout killswitch upfront so the auto-expand below can branch correctly
        const useBraintreePayPalCheckout = PayPal.isPayPalCheckoutEnabledForLocale();
        this.setState({ useBraintreePayPalCheckout });

        const activeAlternatePayment = this.getActiveAlternatePaymentFromOrder();

        // Map Redux alternatePaymentName to determine effective payment type
        // We were seeing mismatch between the pill vs radio button aka the selected payment method in Redux on page load,
        // this ensures the correct pill is selected based Reedux
        const paymentNameToType = {
            [PAYMENT_TYPES.PAY_AFTERPAY]: ALTERNATE_PAYMENT_TYPES.AFTERPAY,
            [PAYMENT_TYPES.PAY_KLARNA]: ALTERNATE_PAYMENT_TYPES.KLARNA,
            [PAYMENT_TYPES.PAY_VENMO]: ALTERNATE_PAYMENT_TYPES.VENMO,
            [PAYMENT_TYPES.PAY_PAZE]: ALTERNATE_PAYMENT_TYPES.PAZE
        };
        const reduxSelectedType = paymentNameToType[this.props.alternatePaymentName];
        // PayPal is tracked via isPayPalSelected flag, not alternatePaymentName
        const reduxPayPalSelected = isPayPalSelected ? ALTERNATE_PAYMENT_TYPES.PAYPAL : null;
        const effectiveAlternatePayment = reduxSelectedType || reduxPayPalSelected || activeAlternatePayment;

        // Auto-select the active-or-default card first; the if/else chain below may override via setState
        // ordering when its conditions match. This mirrors the legacy order where CreditCardsList's
        // componentDidMount (which owned this call) ran before PaymentMethodList's componentDidMount.
        const shouldAutoSelectDefaultCard =
            !giftCardAmountEqualsOrderTotal &&
            !this.state.selectedCreditCardId &&
            !alternateMethodSelected &&
            !checkoutUtils.isPaymentInOrderComplete();

        if (shouldAutoSelectDefaultCard) {
            this.selectDefaultCreditCard();
        }

        if (effectiveAlternatePayment && !hasPendingCreditCard) {
            this.setState({
                selectedAlternatePaymentType: effectiveAlternatePayment,
                selectedCreditCardId: null
            });
        } else if ((!creditCardPaymentGroup.isComplete && !hasPendingCreditCard && this.state.selectedCreditCardId) || paymentName) {
            this.newCreditCard = {};
            this.setState({
                selectedCreditCardId: null,
                securityCode: ''
            });
        } else if ((hasPendingCreditCard || creditCardPaymentGroup.isComplete) && !this.state.isPayWithApplePay && !alternateMethodSelected) {
            // For subscription orders (SDU/AutoReplenish), always auto-select default card
            // even when gift card covers full order total, because card is required for future charges
            const shouldAutoSelectCard = !giftCardAmountEqualsOrderTotal || isSubscriptionOrder;

            if (shouldAutoSelectCard) {
                // Only set CC if no alternate payment selected (prevents resetting Klarna/Afterpay)
                if (!this.state.selectedCreditCardId || this.state.selectedCreditCardId === creditCardPaymentGroup.creditCardId) {
                    this.setNewCreditCard(creditCardPaymentGroup);
                }
            }
        }

        //check if payment section is complete (if terms and conditions have been checked)
        //if not, then if paypal was updated, select paypal radio button or
        //if credit card form is open, close the form
        if (!checkoutUtils.isPaymentSectionComplete()) {
            if (isPayPalSelected && !this.state.isPayWithPayPal) {
                this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);
            }
        }

        // Auto-expand AfterPay if triggered from L1 click
        if (this.props.shouldAutoExpandAfterpay) {
            this.handleAfterPayExpand({ skipLinkTracking: true });
            this.props.clearAutoExpandAfterpay && this.props.clearAutoExpandAfterpay();
        }

        // Auto-expand Klarna if triggered from L1 click
        if (this.props.shouldAutoExpandKlarna) {
            this.handleKlarnaExpand({ skipLinkTracking: true });
            this.props.clearAutoExpandKlarna && this.props.clearAutoExpandKlarna();
        }

        // Auto-expand Paze if triggered from L1 click
        if (this.props.shouldAutoExpandPaze) {
            this.handlePazeExpand({ skipLinkTracking: true });
            this.props.clearAutoExpandPaze && this.props.clearAutoExpandPaze();
        }

        // Auto-expand PayPal if triggered from L1 click
        // Pass the killswitch explicitly because setState above hasn't flushed yet
        if (this.props.shouldAutoExpandPayPal) {
            this.handlePayPalPillClick(useBraintreePayPalCheckout, { skipSotTracking: true });
            this.props.clearAutoExpandPayPal && this.props.clearAutoExpandPayPal();
        }

        // AC1: Track Add New Card button state when payment section loads
        this.trackAddNewCardButtonState();
        this.trackUseThisCardButtonState();

        // Sephora Mirror: agent should see place-order disabled unless they're tier 3 (and have at least one saved card).
        // Lives in PML so it runs even when <CreditCardsList /> doesn't mount (all cards hoisted).
        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            const savedCardCount = this.props.paymentOptions?.creditCards?.length || 0;
            const disablePlaceOrderButton = savedCardCount ? !agentAwareUtils.isAgentAuthorized(['3']) : true;
            this.props.togglePlaceOrderDisabled(disablePlaceOrderButton);
        }
    }

    componentDidUpdate(prevProps) {
        const shouldOpenForm = this.props.openCreditCardForm && !this.state.openCreditCardForm;

        if (prevProps.openCreditCardForm !== this.props.openCreditCardForm && shouldOpenForm) {
            this.setState({ openCreditCardForm: true });
        }

        // Autoexpand AfterPay if flag was set after initial mount
        if (this.props.shouldAutoExpandAfterpay && !prevProps.shouldAutoExpandAfterpay) {
            this.handleAfterPayExpand({ skipLinkTracking: true });
            this.props.clearAutoExpandAfterpay && this.props.clearAutoExpandAfterpay();
        }

        // Auto expand Klarna if flag was set after initial mount
        if (this.props.shouldAutoExpandKlarna && !prevProps.shouldAutoExpandKlarna) {
            this.handleKlarnaExpand({ skipLinkTracking: true });
            this.props.clearAutoExpandKlarna && this.props.clearAutoExpandKlarna();
        }

        // Auto expand Paze if flag was set after initial mount
        if (this.props.shouldAutoExpandPaze && !prevProps.shouldAutoExpandPaze) {
            this.handlePazeExpand({ skipLinkTracking: true });
            this.props.clearAutoExpandPaze && this.props.clearAutoExpandPaze();
        }

        // Auto expand PayPal if flag was set after initial mount
        if (this.props.shouldAutoExpandPayPal && !prevProps.shouldAutoExpandPayPal) {
            this.handlePayPalPillClick(undefined, { skipSotTracking: true });
            this.props.clearAutoExpandPayPal && this.props.clearAutoExpandPayPal();
        }

        // Track Add New Card button state when it becomes visible
        this.trackAddNewCardButtonState();
        this.trackUseThisCardButtonState();

        const giftCardNoLongerCoversOrder = prevProps.giftCardAmountEqualsOrderTotal && !this.props.giftCardAmountEqualsOrderTotal;

        if (giftCardNoLongerCoversOrder && !this.state.selectedCreditCardId && !this.props.alternateMethodSelected) {
            this.selectDefaultCreditCard();
        }
    }

    /**
     * AC1: Track "Add New Card" button state when visible to user.
     * Fires ASYNC_PAGE_LOAD (s.t() TrackState) with pageName format:
     * checkout:payment:n/a:*button=add new card
     *
     * IMPORTANT: Only track for NEW USERS with no saved payment methods.
     * - If user has saved cards, CreditCardsList will auto-select and track "use this card"
     * - If user has saved alternate payments, SavedAlternatePaymentRadio will track "use this payment method"
     * - Only track "add new card" when user truly has NO saved payment methods
     */
    trackAddNewCardButtonState = () => {
        const { shouldDisplayAddOrDeleteCreditCardButton, paymentOptions } = this.props;
        const hasSavedCreditCards = (paymentOptions?.creditCards?.length || 0) > 0;
        const hasSavedAlternatePayments = this.hasSavedAlternatePayment();

        // Only track if:
        // 1. "Add New Card" button is visible
        // 2. Not in an expanded payment flow
        // 3. User has NO saved cards (otherwise CreditCardsList will track "use this card")
        // 4. User has NO saved alternate payments (otherwise SavedAlternatePaymentRadio will track)
        const shouldTrack =
            shouldDisplayAddOrDeleteCreditCardButton &&
            !this.state.openCreditCardForm &&
            !this.state.isAfterPayExpanded &&
            !this.state.isKlarnaExpanded &&
            !this.state.isVenmoExpanded &&
            !this.state.isPazeExpanded &&
            !hasSavedCreditCards &&
            !hasSavedAlternatePayments;

        if (shouldTrack && !this.hasTrackedButtonState) {
            this.hasTrackedButtonState = true;
            FrictionlessCheckoutBindings.setPaymentSectionButtonStateAnalytics(anaConsts.ACTION_INFO.PAYMENT_ADD_NEW_CARD);
        }
    };

    /**
     * Mirrors the unavailable-payment-message logic from NewUserPaymentView so PML can include
     * eVar140 in the "use this card" ASYNC_PAGE_LOAD without a second analytics call.
     * Apple Pay is omitted because its support check is async and not tracked in this class component.
     */
    getUnavailablePaymentMessageForAnalytics = () => {
        const {
            isKlarnaEnabledForThisOrder,
            isAfterpayEnabledForThisOrder,
            isAfterpayEnabledForThisProfile,
            isPazeEnabledForThisOrder,
            isPayPalEnabled,
            isVenmoEnabledForThisOrder,
            paymentLocales,
            orderDetails,
            physicalGiftCardShippingGroup
        } = this.props;

        const giftCardPaymentGroups = OrderUtils.getGiftCardPaymentGroups(orderDetails);
        const hasAppliedGiftCards = giftCardPaymentGroups.length > 0;

        // Apply the same gift-card flag adjustments that PML passes to NewUserPaymentView
        const effectiveKlarnaEnabled = hasAppliedGiftCards ? false : isKlarnaEnabledForThisOrder;
        const effectiveAfterpayOrderEnabled = hasAppliedGiftCards ? false : isAfterpayEnabledForThisOrder;
        const effectiveAfterpayProfileEnabled = hasAppliedGiftCards ? false : isAfterpayEnabledForThisProfile;
        const effectivePazeEnabled = hasAppliedGiftCards ? false : isPazeEnabledForThisOrder;

        const methods = [];

        if (!isPayPalEnabled) {
            methods.push('PayPal');
        }

        if (!effectiveKlarnaEnabled) {
            methods.push('Klarna');
        }

        if (!effectiveAfterpayOrderEnabled || !effectiveAfterpayProfileEnabled) {
            methods.push('Afterpay');
        }

        if (!isVenmoEnabledForThisOrder && !localeUtils.isCanada()) {
            methods.push('Venmo');
        }

        if (pazeUtils.isPazeVisible() && !effectivePazeEnabled) {
            methods.push('Paze');
        }

        if (methods.length === 0) {
            return '';
        }

        let methodsText;

        if (methods.length === 1) {
            methodsText = methods[0];
        } else if (methods.length === 2) {
            methodsText = `${methods[0]} and ${methods[1]}`;
        } else {
            methodsText = `${methods.slice(0, -1).join(', ')} and ${methods[methods.length - 1]}`;
        }

        const reason = hasAppliedGiftCards
            ? paymentLocales.cannotBeCombinedWithGiftCards
            : physicalGiftCardShippingGroup
                ? paymentLocales.cannotBeUsedWhenPurchasingGiftCards
                : paymentLocales.cantUseForThisOrder;

        return `${methodsText} ${reason}`.trim();
    };

    /**
     * AC1: Track "Use This Card" button state when a saved card is selected. Lives in PML so it fires
     * regardless of whether the active card is rendered in the hoisted priority slot or inside the
     * remaining <CreditCardsList> (which may not even mount when all cards are hoisted).
     */
    trackUseThisCardButtonState = () => {
        const shouldTrack = !!this.state.selectedCreditCardId;

        if (shouldTrack && !this.hasTrackedUseThisCardState) {
            this.hasTrackedUseThisCardState = true;
            FrictionlessCheckoutBindings.setPaymentSectionButtonStateAnalytics(
                anaConsts.ACTION_INFO.PAYMENT_USE_THIS_CARD,
                this.getUnavailablePaymentMessageForAnalytics()
            );
        }
    };

    getActiveAlternatePaymentFromOrder = () => {
        return FrictionlessUtils.getActiveAlternatePaymentFromOrder(this.props.orderDetails);
    };

    hasSavedAlternatePayment = () => {
        return FrictionlessUtils.hasSavedOrDefaultAlternatePayment(this.props.paymentOptions, this.props.orderDetails);
    };

    getSavedAlternatePaymentUsername = () => {
        return FrictionlessUtils.getSavedAlternatePaymentUsername(this.props.paymentOptions, this.props.orderDetails);
    };

    getSavedAlternatePayments = () => {
        return FrictionlessUtils.getSavedAlternatePayments(this.props.paymentOptions, this.props.orderDetails, this.props.hasPendingCreditCard);
    };

    getActivePaymentItem = () => {
        const { paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard } = this.props;

        return FrictionlessUtils.getActivePaymentItem(paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard);
    };

    getDefaultPaymentItem = activeItem => {
        const { paymentOptions, orderDetails, hasPendingCreditCard } = this.props;

        return FrictionlessUtils.getDefaultPaymentItem(activeItem, paymentOptions, orderDetails, hasPendingCreditCard);
    };

    getPriorityPaymentItems = () => {
        const { paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard } = this.props;

        return FrictionlessUtils.getPriorityPaymentItems(paymentOptions, orderDetails, creditCardPaymentGroup, hasPendingCreditCard);
    };

    /**
     * Determines if a saved alternate payment method is available for this order.
     * Maps payment type to corresponding availability flags.
     */
    isPaymentMethodAvailable = paymentType => {
        const {
            isPayPalEnabled,
            isVenmoEnabledForThisOrder,
            isKlarnaEnabledForThisOrder,
            isAfterpayEnabledForThisOrder,
            isAfterpayEnabledForThisProfile,
            isPazeEnabledForThisOrder
        } = this.props;

        switch (paymentType) {
            case ALTERNATE_PAYMENT_TYPES.PAYPAL:
                return isPayPalEnabled;
            case ALTERNATE_PAYMENT_TYPES.VENMO:
                return isVenmoEnabledForThisOrder;
            case ALTERNATE_PAYMENT_TYPES.KLARNA:
                return isKlarnaEnabledForThisOrder;
            case ALTERNATE_PAYMENT_TYPES.AFTERPAY:
                return isAfterpayEnabledForThisOrder && isAfterpayEnabledForThisProfile;
            case ALTERNATE_PAYMENT_TYPES.PAZE:
                return isPazeEnabledForThisOrder;
            default:
                return true;
        }
    };

    handleAlternatePaymentClick = paymentType => {
        this.setState({
            selectedAlternatePaymentType: paymentType,
            selectedCreditCardId: null
        });
    };

    handleUseThisPaymentMethod = () => {
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();

        FrictionlessCheckoutBindings.trackPaymentCtaClick({
            selectedAlternatePaymentType: this.state.selectedAlternatePaymentType
        });

        const paymentToUse = this.state.selectedAlternatePaymentType;

        switch (paymentToUse) {
            case ALTERNATE_PAYMENT_TYPES.VENMO:
                Venmo.prepareVenmoCheckout();
                Venmo.initializeVenmoCheckout();
                this.props.commonOrderToggleActions({ venmo: true });
                this.props.updatePaymentActions && this.props.updatePaymentActions(false, false);

                break;

            case ALTERNATE_PAYMENT_TYPES.KLARNA:
                this.props.KlarnaBackgroundInit(Empty.String);
                this.props.commonOrderToggleActions({ klarna: true });
                this.props.klarnaSetReady(true);

                break;

            case ALTERNATE_PAYMENT_TYPES.AFTERPAY:
                this.props.setPaymentMethodAfterPay();
                this.props.commonOrderToggleActions({ afterpay: true });
                this.props.afterpaySetReady(true);

                break;

            case ALTERNATE_PAYMENT_TYPES.PAZE:
                this.props.loadPaze(Empty.Object);
                this.props.commonOrderToggleActions({ paze: true });
                this.props.pazeSetReady(true);
                this.props.updatePaymentActions(true, false);

                break;

            case ALTERNATE_PAYMENT_TYPES.PAYPAL:
                // If PayPal is already saved and selected in order, just close edit mode to show L1
                // Otherwise, use PayPal checkout flow to select PayPal
                this.props.commonOrderToggleActions({ paypal: this.props.isPayPalSelected });

                if (!this.props.isPayPalSelected) {
                    this.handlePayPalPillClick();
                } else {
                    // PayPal payment group already exists on the order (user previously approved
                    // in the PayPal sheet, came back via L1 → "Change" → L2 → "Use This Payment Method").
                    // The Braintree SDK setup that normally dispatches setReady(true) does not run
                    // on this path, so explicitly mark PayPal as ready — otherwise
                    // PlaceOrderButton.getDisabledState() returns true and the CTA stays disabled.
                    this.props.paypalSetReady(true);
                }

                break;

            default:
                break;
        }

        this.props.setEditChangeLink(false);

        if (this.props.saveBtnSuccessCallback) {
            this.props.saveBtnSuccessCallback();
        }
    };

    toggleOpenCreditCardForm =
        (closeHandler = false) =>
            e => {
                e?.preventDefault();
                const { paymentOptions, locales, showInfoModal } = this.props;
                const numberOfCreditCards = paymentOptions?.creditCards?.length || 0;

                // Check if user has reached the maximum number of saved cards
                if (numberOfCreditCards >= MAX_CARDS_LIMIT && !closeHandler) {
                    showInfoModal({
                        isOpen: true,
                        title: locales.maximumReached,
                        message: locales.maxCardsReachedMessage,
                        buttonText: locales.ok,
                        showCloseButton: true
                    });
                } else {
                    this.setState(
                        prevState => ({
                            openCreditCardForm: !prevState.openCreditCardForm,
                            isEditCCFlow: false
                        }),
                        () => {
                            if (this.state?.openCreditCardForm) {
                                const pageDetailPrefix = this.props.isPhysicalGiftCardOnly ? 'gift card ' : '';
                                const pageDetail = pageDetailPrefix + anaConsts.PAGE_NAMES.ADD_CREDIT_CARD;
                                const pageName = `${anaConsts.PAGE_TYPES.CHECKOUT}:${pageDetail}:n/a:*`;

                                checkoutBindings.processAsyncPageLoad(pageDetail);
                                FrictionlessCheckoutBindings.setAnalyticsForNextCall(pageName, pageDetail);
                            }
                        }
                    );
                }
            };

    handlePayWithApplePayClick = Debounce.preventDoubleClick(() => {
        this.switchPaymentState(PAYMENT_FLAGS.isPayWithApplePay);
        this.props.swapPaypalToCreditCard();

        FrictionlessCheckoutBindings.sotPaymentOptionCheckout(anaConsts.ACTION_INFO.VENDOR_APPLEPAY);

        if (checkoutUtils.isGuestOrder()) {
            ApplePay.enableGuestCheckout();
        }

        this.processPaymentClickEvent('checkout:payment:applepay');
        ApplePay.onApplePayClicked();
    });

    // Handles PayPal pill click in L2. Uses killswitch to choose between new expanded view or legacy modal flow.
    // Accepts an optional boolean override so callers (e.g. componentDidMount auto-expand) can pass the killswitch
    // before setState has flushed. Only a boolean is accepted — when used as a click handler the first arg is a
    // SyntheticEvent, which must not be treated as the override value.
    // Programmatic callers (auto-expand) should pass { skipSotTracking: true } to avoid recording a payment method
    // selection event when there was no real user click on the PayPal pill.
    handlePayPalPillClick = Debounce.preventDoubleClick((useBraintreePayPalCheckoutOverride, options = {}) => {
        const { locales } = this.props;
        const { skipSotTracking = false } = options;

        if (OrderUtils.containsRestrictedItem()) {
            this.props.setCheckoutSectionErrors({ paymentMethod: locales.paypalRestrictedItemError });

            return;
        }

        const useBraintreePayPalCheckout =
            typeof useBraintreePayPalCheckoutOverride === 'boolean' ? useBraintreePayPalCheckoutOverride : this.state.useBraintreePayPalCheckout;

        if (!skipSotTracking) {
            PayPalExpressBindings.trackSOTPayPalClick({
                eventName: CHECKOUT.PAYMENT_METHOD_SELECTION,
                sotType: CHECKOUT.PAYMENT_METHOD_SELECTION,
                pageName: CONTEXT.CHECKOUT_PAGE,
                origin: CHECKOUT.PAYMENT_STEP,
                paymentMethod: ACTION_INFO.VENDOR_PAYPAL
            });
        }

        if (useBraintreePayPalCheckout) {
            // NEW: When killswitch is TRUE, use expanded view pattern
            this.handlePayPalExpand();
        } else {
            // LEGACY: When killswitch is FALSE, use old PayPal modal + interstice flow
            PayPal.showPayPal(payload => {
                this.props.payPalWithInterstice(payload).then(() => {
                    this.props.commonOrderToggleActions();
                    this.props.updatePaymentActions();
                    this.props.clearPendingCreditCard();
                    this.props.sectionSaveOrderAction(Location.getLocation().pathname, this);
                    refreshCheckout()().then(() => {
                        this.props.setEditChangeLink(false);
                        this.props.saveBtnSuccessCallback?.();
                    });

                    if (checkoutUtils.isGuestOrder()) {
                        this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);
                    }
                });
            });

            this.processPaymentClickEvent('checkout:express-payment:paypal');
        }
    });

    handlePaymentClick = paymentType =>
        Debounce.preventDoubleClick(() => {
            this.switchPaymentState(paymentType);
            const paymentName = OrderUtils.getPaymentNameByType(paymentType);
            this.processPaymentClickEvent(`checkout:payment:${paymentName}`);
        });

    switchPaymentState = selectedPayment => {
        const newState = { selectedCreditCardId: null };
        Object.keys(PAYMENT_FLAGS).forEach(x => (newState[x] = selectedPayment === PAYMENT_FLAGS[x]));
        this.setState(newState, () => this.updatePayment(selectedPayment));

        processEvent.process(anaConsts.ADD_PAYMENTINFO_EVENT, {
            data: {
                paymentType: selectedPayment
            }
        });
    };

    updatePayment = selectedPayment => {
        const { updatePaymentActions, commonOrderToggleActions } = this.props;

        commonOrderToggleActions({
            isApplePayFlow: selectedPayment === PAYMENT_FLAGS.isPayWithApplePay,
            klarna: selectedPayment === PAYMENT_FLAGS.isPayWithKlarna,
            afterpay: selectedPayment === PAYMENT_FLAGS.isPayWithAfterpay,
            venmo: selectedPayment === PAYMENT_FLAGS.isPayWithVenmo,
            paypal: selectedPayment === PAYMENT_FLAGS.isPayWithPayPal,
            paze: selectedPayment === PAYMENT_FLAGS.isPayWithPaze
        });

        updatePaymentActions(selectedPayment === PAYMENT_FLAGS.isPayWithPaze, selectedPayment === PAYMENT_FLAGS.isPayWithApplePay);
    };

    setNewCreditCard = creditCardData => {
        const {
            utilMerge, klarnaToggle, afterPayToggle, pazeToggle, venmoToggle, afterPayResetError
        } = this.props;
        const isMarkAsDefault = Boolean(creditCardData.isDefault);
        this.newCreditCard = {
            creditCard: creditCardUtils.cleanCreditCardData(creditCardData),
            isMarkAsDefault
        };

        utilMerge('order', 'isApplePayFlow', false);
        klarnaToggle(false);
        afterPayToggle(false);
        pazeToggle(false);
        venmoToggle(false);
        afterPayResetError();

        FrictionlessCheckoutBindings.sotPaymentOptionCheckout(anaConsts.ACTION_INFO.CREDIT_CARD);

        this.setState({
            selectedCreditCardId: creditCardData.creditCardId,
            isPayWithPayPal: false,
            isPayWithApplePay: false,
            selectedAlternatePaymentType: null,
            securityCode: ''
        });
    };

    selectDefaultCreditCard = () => {
        const selectedCard = FrictionlessUtils.findCardToAutoSelect(this.props.paymentOptions?.creditCards);

        if (selectedCard) {
            this.setNewCreditCard(selectedCard);
        }
    };

    handleSetNewCreditCard = creditCard => () => {
        this.setNewCreditCard(creditCard);
    };

    handleShowEditCreditCardForm = creditCard => () => {
        this.showEditCreditCardForm(creditCard);
    };

    trackRemoveCreditCardSuccess = () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                linkName: 'D=c55',
                actionInfo: anaConsts.ACTION_INFO.CREDIT_CARD_REMOVE
            }
        });
    };

    removeOrderPaymentForCard = (creditCardId, orderId) => {
        if (!creditCardUtils.isSavedToProfile(creditCardId)) {
            // eslint-disable-next-line no-param-reassign
            creditCardId = null;
        }

        const creditCardGroupId = this.props.creditCardPaymentGroup.paymentGroupId || 0;

        this.props
            .removeOrderPayment(orderId, creditCardGroupId, creditCardId)
            .then(() => {
                this.trackRemoveCreditCardSuccess();

                this.props.getOrderDetails(orderId).then(newOrderDetails => {
                    this.props.updateOrder(newOrderDetails);
                });

                this.props.getCreditCards(orderId).then(paymentOptions => {
                    this.props.merge('order', 'paymentOptions', paymentOptions);
                });
            })
            .catch(errorData => ErrorsUtils.collectAndValidateBackEndErrors(errorData, this));
    };

    removeCreditCardFromAccount = (creditCardId, orderId) => {
        const profileId = userUtils.getProfileId();
        this.props
            .removeCreditCardFromProfile(profileId, creditCardId)
            .then(() => {
                this.trackRemoveCreditCardSuccess();

                this.props.getCreditCards(orderId).then(paymentOptions => {
                    this.props.merge('order', 'paymentOptions', paymentOptions);
                });
            })
            .catch(response => {
                this.showUnableToDeleteCardModal(response?.errorMessages?.[0]);
            });
    };

    deleteCreditCard = creditCard => {
        const orderId = OrderUtils.getOrderId();
        const nextDefaultCard = FrictionlessUtils.findNextDefaultCardCandidate(this.props.paymentOptions?.creditCards, creditCard.creditCardId);
        const removeCallback = creditCard.isCardInOrder ? this.removeOrderPaymentForCard : this.removeCreditCardFromAccount;

        if (creditCard.isDefault && nextDefaultCard) {
            this.props.setDefaultCreditCardOnProfile(nextDefaultCard.creditCardId).then(() => {
                removeCallback(creditCard.creditCardId, orderId);
            });
        } else {
            removeCallback(creditCard.creditCardId, orderId);
        }
    };

    handleDeleteCreditCard = creditCard => () => {
        this.deleteCreditCard(creditCard);
    };

    showRemoveCreditCardModal = (creditCard, e) => {
        const { removeCreditCard, areYouSureMessage, remove, cancel } = this.props.locales;

        e.preventDefault();

        checkoutBindings.processAsyncPageLoad(anaConsts.PAGE_NAMES.PAYMENT_REMOVE_CREDIT_CARD);

        const callback = () => this.deleteCreditCard(creditCard);

        this.props.showInfoModal({
            isOpen: true,
            title: removeCreditCard,
            message: areYouSureMessage,
            buttonText: remove,
            callback: callback,
            cancelText: cancel,
            showCloseButton: true,
            dataAtTitle: 'remove_credit_card_title',
            dataAtButton: 'remove_credit_card_btn',
            dataAtCancelButton: 'remove_credit_card_cancel_btn',
            bodyFooterPaddingX: 4,
            bodyPaddingBottom: 4,
            showCancelButtonLeft: true
        });
    };

    showUnableToDeleteCardModal = message => {
        const { ok, deleteCard } = this.props.locales;

        this.props.showInfoModal({
            isOpen: true,
            title: deleteCard,
            message: message,
            buttonText: ok,
            showCloseButton: true,
            dataAtTitle: 'unable_to_delete_credit_card_title',
            dataAtButton: 'unable_to_delete_credit_card_btn',
            bodyFooterPaddingX: 4,
            bodyPaddingBottom: 4
        });
    };

    showRemoveCreditCardModalDebounce = Debounce.preventDoubleClick(this.showRemoveCreditCardModal);

    handleRemoveCreditCard = creditCard => e => {
        this.showRemoveCreditCardModalDebounce(creditCard, e);
    };

    processPaymentClickEvent = actionInfo => {
        const eventData = {
            eventStrings: [anaConsts.Event.EVENT_71],
            linkName: 'D=c55',
            actionInfo: actionInfo
        };

        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');

        if (mostRecentAsyncLoadEvent) {
            eventData.previousPage = mostRecentAsyncLoadEvent.eventInfo.attributes.previousPageName;
        }

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: eventData });
    };

    processVendorSaveAndContinueEvent = vendor => {
        const selectedDefault = store.getState().user?.selectedAsDefaultPaymentName?.toLowerCase();
        const isDefaultPayment = !!selectedDefault && selectedDefault === vendor.toLowerCase();
        FrictionlessCheckoutBindings.trackPaymentVendorInteraction(`${anaConsts.ACTION_INFO.SAVE_AND_CONTINUE}:${vendor}`, { isDefaultPayment });
    };

    processVendorExpandClickEvent = vendor => {
        FrictionlessCheckoutBindings.trackPaymentVendorInteraction(`${anaConsts.ACTION_INFO.CHECKOUT_PAYMENT}:${vendor}`);
    };

    /**
     * Triggered when user clicks the Afterpay pill button.
     * Opens the AfterPayExpandedView component inline.
     * Widget loading is handled by AfterPayExpandedView after mount.
     *
     * @async
     */
    handleAfterPayExpand = async ({ skipLinkTracking = false } = {}) => {
        const widgetElementId = 'afterpay-widget-expanded-view-pml';

        // Set expanded state with loading indicator and the widget element ID
        // The element ID is set immediately so the DOM element renders
        // IMPORTANT: Clear other expanded states to ensure only one is visible at a time
        this.setState({
            isAfterPayExpanded: true,
            isAfterPayLoading: true,
            afterpayError: null,
            afterpayApiData: { widgetElementId },
            // Clear other expanded states
            isKlarnaExpanded: false,
            isVenmoExpanded: false,
            isPazeExpanded: false,
            isPayPalExpanded: false
        });

        // Track L2 payment button click
        if (!skipLinkTracking) {
            this.processVendorExpandClickEvent('afterpay');
        }

        FrictionlessCheckoutBindings.sotPaymentOptionCheckout(anaConsts.ACTION_INFO.VENDOR_AFTERPAY);
    };

    /**
     * Callback for AfterPayExpandedView to load the widget after mount.
     * This ensures the DOM element exists before calling the SDK.
     */
    loadAfterPayWidget = async () => {
        const { orderDetails, loadExpandedViewWidget } = this.props;
        const priceInfo = orderDetails?.priceInfo;
        // Try multiple fields to get the order amount bc the SDK requires a positive amount
        const orderTotal = priceInfo?.creditCardAmount || priceInfo?.paypalAmount || priceInfo?.orderTotal || 0;
        const widgetElementId = 'afterpay-widget-expanded-view-pml';

        // Afterpay SDK requires a positive amount, show error if amount is 0 or invalid
        if (!orderTotal || orderTotal === '$0.00' || orderTotal === '0') {
            this.setState({
                isAfterPayLoading: false,
                afterpayError: 'Unable to calculate payment schedule. Please try again.'
            });

            return;
        }

        if (loadExpandedViewWidget) {
            // Set a timeout fallback in case SDK never calls onReady
            const timeoutId = setTimeout(() => {
                this.setState({ isAfterPayLoading: false });
            }, 10000);

            try {
                await loadExpandedViewWidget(widgetElementId, orderTotal);
                clearTimeout(timeoutId);
                this.setState({ isAfterPayLoading: false });
            } catch (error) {
                clearTimeout(timeoutId);
                this.setState({
                    isAfterPayLoading: false,
                    afterpayError: error?.message || 'Unable to load Afterpay information. Please try again.'
                });
            }
        } else {
            this.setState({ isAfterPayLoading: false });
        }
    };

    /**
     * Triggered when user clicks "Save and Continue" button in AfterPayExpandedView.
     * Collapses the L3 expanded view, selects Afterpay as payment method, and returns to L1 state
     * Sets Afterpay as ready so PlaceOrderButton shows "Continue with Afterpay"
     */
    handleAfterPaySaveAndContinue = () => {
        // Collapse expanded view
        this.setState({ isAfterPayExpanded: false });

        // Select Afterpay as payment method (sets Redux state)
        // Call commonOrderToggleActions directly instead of through switchPaymentState
        // because switchPaymentState uses a setState callback which creates a race condition
        // with the navigation below. The Redux state must be updated synchronously before navigating.
        this.props.commonOrderToggleActions({ afterpay: true });
        this.props.updatePaymentActions(false, false);
        this.props.setPaymentMethodAfterPay(this.props.locales.afterpayErrorMessage);
        this.props.afterpaySetReady && this.props.afterpaySetReady(true);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();

        // Navigate back to L1 state (close edit mode)
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback();

        // Track save and continue click
        this.processVendorSaveAndContinueEvent('afterpay');
    };

    /**
     * Returns user to L2 edit mode.
     */
    handleAfterPayCancel = () => {
        // Collapse expanded view and clear all Afterpay data
        this.setState({
            isAfterPayExpanded: false,
            afterpayApiData: null, // Clear widget data
            afterpayError: null // Clear any errors
        });

        // Clear Afterpay ready state when canceled
        this.props.afterpaySetReady(false);

        // Analytics - track user canceling Afterpay flow
        this.processPaymentClickEvent('checkout:payment:afterpay:cancel');
    };

    /**
     * Triggered when user clicks the Klarna pill button.
     * Opens the KlarnaExpandedView component inline.
     */
    handleKlarnaExpand = async ({ skipLinkTracking = false }) => {
        const widgetElementId = 'klarna-widget-expanded-view-pml';

        // IMPORTANT: Clear other expanded states to ensure only one is visible at a time
        this.setState({
            isKlarnaExpanded: true,
            isKlarnaLoading: true,
            klarnaError: null,
            klarnaApiData: { widgetElementId },
            // Clear other expanded states
            isAfterPayExpanded: false,
            isVenmoExpanded: false,
            isPazeExpanded: false,
            isPayPalExpanded: false
        });

        // Track L2 payment button click
        if (!skipLinkTracking) {
            this.processVendorExpandClickEvent('klarna');
        }

        FrictionlessCheckoutBindings.sotPaymentOptionCheckout(anaConsts.ACTION_INFO.VENDOR_KLARNA);
    };

    /**
     * Loads Klarna SDK widget after DOM element is ready.
     * Uses shared Util to keep code dry.
     */
    loadKlarnaWidget = async () => {
        const loader = createKlarnaWidgetLoader({
            widgetElementId: 'klarna-widget-expanded-view-pml',
            setState: this.setState.bind(this),
            klarnaSetReady: this.props.klarnaSetReady,
            locales: this.props.locales
        });

        return loader();
    };

    /**
     * Triggered when user clicks "Save and Continue" button in KlarnaExpandedView.
     * Collapses the L3 expanded view, selects Klarna as payment method, and returns to L1 state.
     * Sets Klarna as ready so PlaceOrderButton shows "Continue with Klarna"
     */
    handleKlarnaSaveAndContinue = () => {
        this.setState({ isKlarnaExpanded: false });
        // Call commonOrderToggleActions directly instead of through switchPaymentState
        // because switchPaymentState uses a setState callback which creates a race condition
        // with the navigation below. The Redux state must be updated synchronously before navigating.
        this.props.commonOrderToggleActions({ klarna: true });
        this.props.updatePaymentActions(false, false);
        this.props.klarnaSetReady && this.props.klarnaSetReady(true);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();

        // Navigate back to L1 state (close edit mode)
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback();

        // Track save and continue click
        this.processVendorSaveAndContinueEvent('klarna');
    };

    /**
     * Triggered when user clicks "Cancel" button in KlarnaExpandedView.
     * Collapses the L3 expanded view without selecting Klarna.
     * Returns user to L2 edit mode.
     */
    handleKlarnaCancel = () => {
        this.setState({
            isKlarnaExpanded: false,
            klarnaApiData: null,
            klarnaError: null
        });

        // Deselect only Klarna in Redux, preserving any other alternate payment selection
        this.props.klarnaToggle?.(false);
        this.props.klarnaSetReady?.(false);

        this.processPaymentClickEvent('checkout:payment:klarna:cancel');
    };

    /**
     * Triggered when user clicks the Venmo pill button.
     * Opens the Venmo expanded view inline.
     */
    handleVenmoExpand = () => {
        FrictionlessCheckoutBindings.sotPaymentOptionCheckout(anaConsts.ACTION_INFO.VENDOR_VENMO);

        Venmo.prepareVenmoCheckout();
        // IMPORTANT: Clear other expanded states to ensure only one is visible at a time
        this.setState({
            isVenmoExpanded: true,
            // Clear other expanded states
            isKlarnaExpanded: false,
            isAfterPayExpanded: false,
            isPazeExpanded: false,
            isPayPalExpanded: false
        });

        // Track L2 payment button click
        this.processVendorExpandClickEvent('venmo');
    };

    /**
     * Triggered when user clicks the PayPal pill button.
     * Opens the PayPal expanded view inline.
     */
    handlePayPalExpand = () => {
        PayPal.preparePaypalCheckout();
        this.setState({
            isPayPalExpanded: true,
            isKlarnaExpanded: false,
            isAfterPayExpanded: false,
            isPazeExpanded: false,
            isVenmoExpanded: false
        });

        // Track L2 payment button click
        this.processVendorExpandClickEvent('paypal');
    };

    /**
     * Triggered when user clicks the Paze pill button.
     * Opens the Paze expanded view inline.
     */
    handlePazeExpand = ({ skipLinkTracking = false }) => {
        FrictionlessCheckoutBindings.sotPaymentOptionCheckout(anaConsts.ACTION_INFO.VENDOR_PAZE);

        this.setState({
            isPazeExpanded: true,
            isKlarnaExpanded: false,
            isAfterPayExpanded: false,
            isVenmoExpanded: false,
            isPayPalExpanded: false
        });

        // Track L2 payment button click
        if (!skipLinkTracking) {
            this.processVendorExpandClickEvent('paze');
        }
    };

    /**
     * Triggered when user clicks "Save and Continue" in Paze expanded view.
     * Selects Paze and returns to L1 state.
     */
    handlePazeSaveAndContinue = () => {
        this.setState({ isPazeExpanded: false });
        this.props.loadPaze(Empty.Object);
        this.props.commonOrderToggleActions();
        this.props.updatePaymentActions(true, false);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback && this.props.saveBtnSuccessCallback();

        // Track save and continue click
        this.processVendorSaveAndContinueEvent('paze');
    };

    /**
     * Triggered when user clicks "Cancel" in Paze expanded view.
     * Collapses the L3 expanded view and keeps Paze as the active payment
     * if it was previously saved. No Redux state change is needed because
     * handlePazeExpand does not set any Redux state to begin with.
     */
    handlePazeCancel = () => {
        this.setState({ isPazeExpanded: false });
        this.processPaymentClickEvent('checkout:payment:paze:cancel');
    };

    /**
     * Triggered when user clicks "Save and Continue" in Venmo expanded view.
     * Selects Venmo and returns to L1 state.
     */
    handleVenmoSaveAndContinue = () => {
        Venmo.initializeVenmoCheckout();
        this.setState({ isVenmoExpanded: false });
        this.switchPaymentState(PAYMENT_FLAGS.isPayWithVenmo);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback && this.props.saveBtnSuccessCallback();

        // Track save and continue click
        this.processVendorSaveAndContinueEvent('venmo');
    };

    /**
     * Triggered when user clicks "Cancel" in Venmo expanded view.
     * Collapses the L3 expanded view without selecting Venmo.
     * Returns user to L2 edit mode.
     */
    handleVenmoCancel = () => {
        this.setState({ isVenmoExpanded: false });
        this.processPaymentClickEvent('checkout:payment:venmo:cancel');
    };

    /**
     * Triggered when user clicks "Save and Continue" in PayPal expanded view.
     * Uses killswitch to determine flow: new pattern vs legacy popup.
     */
    handlePayPalSaveAndContinue = () => {
        const { useBraintreePayPalCheckout } = this.state;

        PayPalExpressBindings.trackSOTPayPalClick({
            eventName: CHECKOUT.PAYPAL_SAVE_AND_CONTINUE_CLICK,
            sotType: CHECKOUT.PAYPAL_SAVE_AND_CONTINUE_CLICK,
            pageName: Location.isCheckout() ? CONTEXT.CHECKOUT_PAGE : CONTEXT.BASKET_PAGE,
            origin: CHECKOUT.PAYMENT_STEP,
            paymentMethod: ACTION_INFO.VENDOR_PAYPAL
        });

        if (useBraintreePayPalCheckout) {
            // NEW: When killswitch is TRUE, initialize PayPal payment group
            PayPalCheckout.initializePayPalPaymentGroup();
            this.setState({ isPayPalExpanded: false });
            this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);
            this.props.clearPendingCreditCard?.();
            this.props.setEditChangeLink(false);
            this.props.saveBtnSuccessCallback?.();
            this.processVendorSaveAndContinueEvent('paypal');
        } else {
            // LEGACY: When killswitch is FALSE, open PayPal popup
            PayPal.showPayPal(payload => {
                this.props.payPalWithInterstice(payload).then(() => {
                    this.props.commonOrderToggleActions();
                    this.props.updatePaymentActions();
                    this.props.clearPendingCreditCard();
                    this.props.sectionSaveOrderAction(Location.getLocation().pathname, this);
                    refreshCheckout()().then(() => {
                        this.props.setEditChangeLink(false);
                        this.props.saveBtnSuccessCallback?.();
                    });

                    if (checkoutUtils.isGuestOrder()) {
                        this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);
                    }

                    this.setState({ isPayPalExpanded: false });
                });
            });

            this.processPaymentClickEvent('checkout:express-payment:paypal');
        }
    };

    handlePayPalDefaultPaymentMethodSelected = action => {
        PayPalExpressBindings.trackSOTSetDefaultPayment({
            eventName: CHECKOUT.SET_DEFAULT_PAYMENT,
            sotType: CHECKOUT.SET_DEFAULT_PAYMENT,
            pageName: Location.isCheckout() ? CONTEXT.CHECKOUT_PAGE : CONTEXT.BASKET_PAGE,
            origin: CHECKOUT.PAYMENT_SELECTION,
            paymentMethod: ACTION_INFO.VENDOR_PAYPAL,
            action
        });
    };

    /**
     * Triggered when user clicks "Cancel" in PayPal expanded view.
     * Collapses the L3 expanded view without selecting PayPal.
     * Returns user to L2 edit mode.
     */
    handlePayPalCancel = () => {
        this.setState({ isPayPalExpanded: false });
        this.processPaymentClickEvent('checkout:payment:paypal:cancel');
    };

    /**
     * Toggles billing address checkbox
     */
    handleBillingAddressToggle = () => {
        this.setState(prevState => ({
            useMyShippingAddress: !prevState.useMyShippingAddress
        }));
    };

    showEditCreditCardForm = prevCreditCardInfo => {
        const creditCardInfo = deepExtend({}, prevCreditCardInfo);

        //format exp month for edit credit card
        if (creditCardInfo.expirationMonth.length === 1) {
            creditCardInfo.expirationMonth = creditCardUtils.formatExpMonth(creditCardInfo.expirationMonth);
        }

        //format exp year for edit credit card
        if (creditCardInfo.expirationYear.length === 4) {
            creditCardInfo.expirationYear = creditCardUtils.formatExpYear(creditCardInfo.expirationYear);
        }

        creditCardInfo.expirationDate = `${creditCardInfo.expirationMonth}/${creditCardInfo.expirationYear}`;

        this.setState(
            {
                openCreditCardForm: true,
                editCreditCard: creditCardInfo,
                isEditCCFlow: true,
                isUseShipAddressAsBilling: creditCardUtils.isShipAddressBillingAddress(this.props.shippingAddress, creditCardInfo.address)
            },
            () => {
                //Analytics
                const pageDetailPrefix = this.props.isPhysicalGiftCardOnly ? 'gift card ' : '';
                checkoutBindings.processAsyncPageLoad(pageDetailPrefix + anaConsts.PAGE_NAMES.EDIT_CREDIT_CARD);
            }
        );
    };

    handleCreditCardFormSave = () => {
        if (this.props.isBopis) {
            this.toggleOpenCreditCardForm(true)();

            return;
        }

        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback && this.props.saveBtnSuccessCallback();
    };

    updateSecurityCode = securityCode => {
        this.setState({ securityCode: securityCode });
    };

    updateCreditCardOnOrder = creditCardToSave => {
        const requestData = FrictionlessUtils.setRequestDataForCreditCardUpdate(creditCardToSave);

        decorators
            .withInterstice(checkoutApi.updateCreditCardOnOrder, INTERSTICE_DELAY_MS)(requestData, 'select')
            .then(creditCardData => {
                if (creditCardData) {
                    this.props.orderMerge('order', 'bankRewardsValidPaymentsMessage', creditCardData.bankRewardsValidPaymentsMessage);
                }

                this.props.swapPaypalToCreditCard();
                refreshCheckout()().then(() => {
                    this.props.clearPendingCreditCard();
                    this.props.setEditChangeLink(false);

                    if (this.props.saveBtnSuccessCallback) {
                        this.props.saveBtnSuccessCallback();
                    }
                });
            })
            .catch(errorData => {
                ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
            });
    };

    saveAndContinue = e => {
        e.preventDefault();
        ErrorsUtils.clearErrors();
        this.props.clearNamedSectionErrors([SECTION_NAMES.PAYMENT]);
        const componentsToValidate = [];

        if (this.creditCardsList?.refs?.securityCodeInput) {
            componentsToValidate.push(this.creditCardsList.refs.securityCodeInput);
        }

        ErrorsUtils.collectClientFieldErrors(componentsToValidate);

        this.props.setPaymentMethodNeedsConfirmation(false);

        if (!ErrorsUtils.validate()) {
            // Handle the case when PayPal is selected
            if (this.state.isPayWithPayPal) {
                this.props.setEditChangeLink(false);
            }

            // Set security code on credit card if present
            if (this.state.securityCode) {
                this.newCreditCard.creditCard.securityCode = this.state.securityCode;
            }

            // Handle the case when a credit card is selected
            if (this.state.selectedCreditCardId) {
                if (this.newCreditCard) {
                    // delete property as it is not required for updateCreditCardOnOrder API
                    delete this.newCreditCard.creditCard?.isCVVValidationRequired;
                }

                const creditCardToSave = this.props.paymentOptions.creditCards.filter(
                    creditCard => creditCard.creditCardId === this.newCreditCard.creditCard.creditCardId
                )?.[0];

                // Save pending credit card to Redux
                this.props.savePendingCreditCard(creditCardToSave);
                this.props.commonOrderToggleActions();
                this.clearSephoraCardEditFlagForAgent();

                // Check if alternate payment method is currently selected
                if (this.props.haslternatePaymentMethodSelected) {
                    const isCVVRequired = FrictionlessUtils.showCVVModal(creditCardToSave, this.props.orderDetails);

                    if (isCVVRequired) {
                        this.props.showConfirmCVVModal({
                            isOpen: true,
                            reason: this.props.confirmCVVReason,
                            successCallback: () => {
                                if (this.props.saveBtnSuccessCallback) {
                                    this.props.saveBtnSuccessCallback();
                                }
                            },
                            isSwitchingFromAlternatePayment: true
                        });
                    } else {
                        this.updateCreditCardOnOrder(creditCardToSave);
                    }
                } else {
                    this.props.setEditChangeLink(false);

                    if (this.props.saveBtnSuccessCallback) {
                        this.props.saveBtnSuccessCallback();
                    }
                }
            } else if (!this.state.isPayWithPayPal) {
                // Handle the case when no credit card or PayPal is selected
                // This happens when a gift card covers the entire order amount
                // Close the edit mode, accepting the gift card as the only payment method

                // For subscription orders without saved card, keep L2 open to add a card
                if (this.props.isSubscriptionOrder && this.props.hasNoSavedCreditCards) {
                    const paymentSectionError =
                        this.props.isCreditCardRequiredMessage ||
                        this.props.sectionErrors?.[SECTION_NAMES.PAYMENT] ||
                        this.props.locales.giftCardAdditionalPaymentRequired;

                    if (paymentSectionError) {
                        FrictionlessCheckoutBindings.setSectionLevelErrorAnalytics(SECTION_NAMES.PAYMENT, paymentSectionError);
                        this.props.setCheckoutSectionErrors({ [SECTION_NAMES.PAYMENT]: paymentSectionError });
                    }

                    return;
                }

                if (this.props.sectionErrors?.Payment) {
                    FrictionlessCheckoutBindings.setSectionLevelErrorAnalytics(SECTION_NAMES.PAYMENT, this.props.sectionErrors?.Payment);
                    this.props.setCheckoutSectionErrors(this.props.sectionErrors);
                } else if (this.props.saveBtnSuccessCallback) {
                    this.props.setEditChangeLink(false);
                    this.props.saveBtnSuccessCallback();
                }
            }
        }
    };

    clearSephoraCardEditFlagForAgent = () => {
        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession() && !agentAwareUtils.isAgentAuthorized(['3'])) {
            Storage.session.setItem(LOCAL_STORAGE.EDIT_SEPHORA_CARD, false);
        }
    };

    saveAndContinueDebounce = Debounce.preventDoubleClick(this.saveAndContinue);

    handleUseThisCardClick = e => {
        FrictionlessCheckoutBindings.trackPaymentCtaClick({
            selectedCardType: this.getSelectedCardTypeForAnalytics()
        });
        this.saveAndContinueDebounce(e);
    };

    handleGiftCardFormVisibility = isVisible => {
        this.setState({ isGiftCardFormOpen: isVisible });
    };

    getShouldRenderTopInlinePaymentError = ({ storeCreditNotEnough, isCreditCardRequiredError }) => {
        const shouldHideTopInlinePaymentError = this.props.isSubscriptionOrder && this.props.hasNoSavedCreditCards;

        return (storeCreditNotEnough || isCreditCardRequiredError) && !shouldHideTopInlinePaymentError;
    };

    // Extract condition to reduce render method complexity
    shouldShowSavedAlternatePayment = isFullyCoveredByAccountCredit => {
        // Hide saved alternate payments when account credit covers full order
        if (isFullyCoveredByAccountCredit) {
            return false;
        }

        return true;
    };

    isGiftCardSectionEligible = ({
        showGiftCard,
        isGuestCheckout,
        isPhysicalGiftCardOnly,
        zeroDollarHigherRedeemPointsCVV,
        alternateMethodSelected,
        orderHasGiftCardItem
    }) => {
        const isPayPalOrVenmoSelected =
            this.state.selectedAlternatePaymentType === ALTERNATE_PAYMENT_TYPES.PAYPAL ||
            this.state.selectedAlternatePaymentType === ALTERNATE_PAYMENT_TYPES.VENMO;
        const isAlternateSelected = (alternateMethodSelected || !!this.state.selectedAlternatePaymentType) && !isPayPalOrVenmoSelected;

        return (
            (showGiftCard || (isGuestCheckout && !isPhysicalGiftCardOnly) || (isPhysicalGiftCardOnly && !isAlternateSelected)) &&
            !isAlternateSelected &&
            !zeroDollarHigherRedeemPointsCVV &&
            (!orderHasGiftCardItem || (orderHasGiftCardItem && !isAlternateSelected))
        );
    };

    renderGiftCardNotCombinableText = () => {
        const { locales } = this.props;

        return (
            <Text
                is='p'
                fontSize='sm'
                color='gray'
                lineHeight='tight'
                marginTop={4}
                data-at={Sephora.debug.dataAt('gift_cards_not_combinable_msg')}
            >
                {locales.giftCardsNotCombinable}
            </Text>
        );
    };

    renderGiftCardNotCombinable = () => (
        <Box>
            <Divider marginY={[3, 4]} />
            <Box marginTop={2}>{this.renderGiftCardNotCombinableText()}</Box>
        </Box>
    );

    renderGiftCard = () => {
        const {
            canRedeemGiftCards,
            orderDetails,
            paymentLocales,
            locales,
            giftCardAmountEqualsOrderTotal,
            showGCAmountNotEnoughMessage,
            isFullyCoveredByAccountCredit,
            hasFreeSDUTrialOnlyInBasket,
            isSubscriptionOrder
        } = this.props;

        // Hide gift card section when account credit covers full order or order contains only gift cards
        if (isFullyCoveredByAccountCredit || OrderUtils.isGiftCardOnly()) {
            return null;
        }

        const giftCardPaymentGroups = OrderUtils.getGiftCardPaymentGroups(orderDetails);
        const hasAppliedGiftCards = giftCardPaymentGroups.length > 0;
        const isGiftCardSectionEligible = this.isGiftCardSectionEligible(this.props);

        if (hasFreeSDUTrialOnlyInBasket) {
            return null;
        }

        if (!isGiftCardSectionEligible && !hasAppliedGiftCards) {
            return this.renderGiftCardNotCombinable();
        }

        return (
            <Box>
                <Divider marginY={[3, 4]} />
                <Box marginTop={2}>
                    {hasAppliedGiftCards && (
                        <>
                            <Text
                                is='h3'
                                fontWeight='bold'
                                marginBottom={[2, 3]}
                            >
                                {paymentLocales.giftCard}
                            </Text>
                            {showGCAmountNotEnoughMessage && (
                                <Text
                                    is='p'
                                    color='red'
                                    marginBottom={[3, 3]}
                                    lineHeight='tight'
                                >
                                    {locales.giftCardAdditionalPaymentRequired}
                                </Text>
                            )}
                            <AppliedGiftCards />
                            {giftCardAmountEqualsOrderTotal && !isSubscriptionOrder && (
                                <Button
                                    variant='primary'
                                    marginTop={4}
                                    width={['100%', '296px']}
                                    onClick={this.saveAndContinueDebounce}
                                >
                                    {locales.afterpaySaveAndContinue}
                                </Button>
                            )}
                        </>
                    )}

                    {giftCardAmountEqualsOrderTotal && !isSubscriptionOrder ? null : canRedeemGiftCards ? (
                        !isGiftCardSectionEligible ? (
                            this.renderGiftCardNotCombinableText()
                        ) : (
                            <UseAGiftCard
                                isEditMode
                                onFormVisibilityChange={this.handleGiftCardFormVisibility}
                                withoutLeftMargin={true}
                                hasTitle={!hasAppliedGiftCards}
                            />
                        )
                    ) : (
                        <Text
                            is='p'
                            color='gray'
                            fontSize='sm'
                            lineHeight='tight'
                            marginTop={4}
                        >
                            {locales.maxGiftCards}
                        </Text>
                    )}
                </Box>
            </Box>
        );
    };

    renderNewPaymentView() {
        const {
            isKlarnaEnabledForThisOrder,
            isVenmoEnabledForThisOrder,
            isAfterpayEnabledForThisOrder,
            isAfterpayEnabledForThisProfile,
            isPazeEnabledForThisOrder,
            isPayPalEnabled,
            isPayPalPayLaterEligible,
            orderDetails,
            isZeroCheckout,
            paymentLocales,
            installmentValue,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            onAlternatePaymentDetails,
            isFullyCoveredByAccountCredit,
            isStoreCreditApplied,
            physicalGiftCardShippingGroup,
            hasSavedOrDefaultAlternatePayment,
            hasFreeSDUTrialOnlyInBasket
        } = this.props;

        const { isGiftCardFormOpen } = this.state;
        const isGiftCardOnly = OrderUtils.isGiftCardOnly();
        const giftCardPaymentGroups = OrderUtils.getGiftCardPaymentGroups(orderDetails);
        const hasAppliedGiftCards = giftCardPaymentGroups.length > 0;
        // NewUserPaymentView renders its own "Other Ways to Pay" divider when any alternate payment is enabled,
        // so suppress this outer divider in that case to avoid a duplicate divider below the "Add new card" link.
        const hasAlternatePayments =
            isPayPalEnabled ||
            isVenmoEnabledForThisOrder ||
            (!hasAppliedGiftCards && (isKlarnaEnabledForThisOrder || isAfterpayEnabledForThisOrder || isPazeEnabledForThisOrder));
        const isDividerHidden =
            (!isGiftCardOnly && !hasFreeSDUTrialOnlyInBasket) || (isGiftCardOnly && hasAlternatePayments && !giftCardAmountEqualsOrderTotal);

        // Hide BNPL options when account credit covers full order
        return (
            !Sephora.isAgent &&
            (!isZeroCheckout || isSubscriptionOrder) &&
            !isGiftCardFormOpen &&
            !isFullyCoveredByAccountCredit && (
                <>
                    {!isDividerHidden && <Divider marginY={[3, 4]} />}
                    {/*Use NewUserPaymentView where Other Ways to Pay section with alternate buttons implemented */}
                    <NewUserPaymentView
                        localization={paymentLocales}
                        availablePaymentMethods={{
                            isPayPalEnabled,
                            isVenmoEnabledForThisOrder,
                            isKlarnaEnabledForThisOrder: hasAppliedGiftCards ? false : isKlarnaEnabledForThisOrder,
                            isAfterpayEnabledForThisOrder: hasAppliedGiftCards ? false : isAfterpayEnabledForThisOrder,
                            isAfterpayEnabledForThisProfile: hasAppliedGiftCards ? false : isAfterpayEnabledForThisProfile,
                            isPazeEnabledForThisOrder: hasAppliedGiftCards ? false : isPazeEnabledForThisOrder,
                            isPayPalPayLaterEligible
                        }}
                        orderDetails={orderDetails}
                        installmentValue={installmentValue}
                        disableContainerPadding
                        withGiftCard={false}
                        giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                        handlePayWithApplePayClick={this.handlePayWithApplePayClick}
                        handlePayPalClick={this.handlePayPalPillClick}
                        handleKlarnaExpand={this.handleKlarnaExpand}
                        handleAfterPayExpand={this.handleAfterPayExpand}
                        handleVenmoExpand={this.handleVenmoExpand}
                        handlePazeExpand={this.handlePazeExpand}
                        giftCardPaymentGroups={giftCardPaymentGroups}
                        isGiftCardOnly={isGiftCardOnly}
                        onAlternatePaymentDetails={onAlternatePaymentDetails}
                        isSubscriptionOrder={isSubscriptionOrder}
                        hasNoSavedCreditCards={hasNoSavedCreditCards}
                        hasSavedAlternatePayments={hasSavedOrDefaultAlternatePayment}
                        isStoreCreditApplied={isStoreCreditApplied}
                        physicalGiftCardShippingGroup={physicalGiftCardShippingGroup}
                    />
                </>
            )
        );
    }

    // L3 State: renders whichever expanded payment view is active (Venmo, Paze, Klarna, AfterPay),
    // or null when none is expanded so the caller can fall through to the main payment form.
    renderExpandedView() {
        const { locales, installmentValue, orderDetails } = this.props;
        const {
            isVenmoExpanded,
            isPayPalExpanded,
            isPazeExpanded,
            isKlarnaExpanded,
            isAfterPayExpanded,
            klarnaApiData,
            isKlarnaLoading,
            klarnaError,
            useMyShippingAddress,
            afterpayApiData,
            isAfterPayLoading,
            afterpayError
        } = this.state;

        if (isVenmoExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <VenmoExpandedView
                        venmoHeader={locales.venmoHeader}
                        venmoSaveAndContinue={locales.venmoSaveAndContinue}
                        venmoCancel={locales.cancel}
                        onSaveAndContinue={this.handleVenmoSaveAndContinue}
                        onCancel={this.handleVenmoCancel}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                    />
                </>
            );
        }

        if (isPayPalExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <PayPalExpandedView
                        paypalHeader={locales.paypalHeader}
                        paypalLegalNotice={locales.paypalLegalNotice}
                        paypalSaveAndContinue={locales.paypalSaveAndContinue}
                        paypalCancel={locales.cancel}
                        onSaveAndContinue={this.handlePayPalSaveAndContinue}
                        onCancel={this.handlePayPalCancel}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                        onDefaultPaymentMethodSelected={this.handlePayPalDefaultPaymentMethodSelected}
                    />
                </>
            );
        }

        if (isPazeExpanded) {
            return (
                <PazeExpandedView
                    pazeHeader={locales.pazeHeader}
                    pazeSubtitle={locales.pazeSubtitle}
                    pazeGiftCardWarning={locales.pazeGiftCardWarning}
                    pazeSaveAndContinue={locales.pazeSaveAndContinue}
                    pazeCancel={locales.pazeCancel}
                    onSaveAndContinue={this.handlePazeSaveAndContinue}
                    onCancel={this.handlePazeCancel}
                />
            );
        }

        if (isKlarnaExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <KlarnaExpandedView
                        klarnaApiData={klarnaApiData}
                        onSaveAndContinue={this.handleKlarnaSaveAndContinue}
                        onCancel={this.handleKlarnaCancel}
                        loadWidget={this.loadKlarnaWidget}
                        klarnaHeader={locales.klarnaHeader}
                        klarnaSubtitle={locales.klarnaSubtitle}
                        klarnaSaveAndContinue={locales.klarnaSaveAndContinue}
                        klarnaCancel={locales.klarnaCancel}
                        klarnaLoading={locales.klarnaLoading}
                        klarnaApiErrorFallback={locales.klarnaApiErrorFallback}
                        klarnaGiftCardWarning={locales.klarnaGiftCardWarning}
                        klarnaFeatureBuyerProtection={locales.klarnaFeatureBuyerProtection}
                        klarnaTermsText={locales.klarnaTermsText}
                        klarnaBillingAddressCheckbox={locales.klarnaBillingAddressCheckbox}
                        isLoading={isKlarnaLoading}
                        errorMessage={klarnaError}
                        installmentValue={installmentValue}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                        isBopisOrder={orderDetails?.header?.isBopisOrder}
                        useMyShippingAddress={useMyShippingAddress}
                        onBillingAddressToggle={this.handleBillingAddressToggle}
                    />
                </>
            );
        }

        if (isAfterPayExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <AfterPayExpandedView
                        afterpayApiData={afterpayApiData}
                        onSaveAndContinue={this.handleAfterPaySaveAndContinue}
                        onCancel={this.handleAfterPayCancel}
                        loadWidget={this.loadAfterPayWidget}
                        afterpayHeader={locales.afterpayHeader}
                        afterpaySubtitle={locales.afterpaySubtitle}
                        afterpaySaveAndContinue={locales.afterpaySaveAndContinue}
                        afterpayCancel={locales.afterpayCancel}
                        afterpayLoading={locales.afterpayLoading}
                        afterpayApiErrorFallback={locales.afterpayApiErrorFallback}
                        afterpayGiftCardWarning={locales.afterpayGiftCardWarning}
                        afterpayLegalNotice={locales.afterpayLegalNotice}
                        isLoading={isAfterPayLoading}
                        errorMessage={afterpayError}
                        installmentValue={installmentValue}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                        isBopisOrder={orderDetails?.header?.isBopisOrder}
                    />
                </>
            );
        }

        return null;
    }

    getErrorMessageState() {
        const {
            isFullyCoveredByAccountCredit,
            isStoreCreditApplied,
            isCreditCardRequiredMessage,
            storeCredits,
            paymentOptions,
            isSubscriptionOrder,
            giftCardPaymentGroups
        } = this.props;

        const isPaymentComplete = checkoutUtils.isPaymentInOrderComplete(true);
        const hasSavedCreditCards = (paymentOptions?.creditCards?.length || 0) > 0;
        const subscriptionNeedsCreditCard = isSubscriptionOrder && !hasSavedCreditCards;
        const shouldTreatAccountCreditAsFinalPayment = isFullyCoveredByAccountCredit && !isSubscriptionOrder;
        const storeCreditNotEnough = storeCredits?.length > 0 && !isPaymentComplete;
        const isCreditCardRequiredError = isCreditCardRequiredMessage?.length > 0 && !paymentOptions?.creditCards?.length;
        // Subscription orders still need a CC for recurring charges even when AC covers the initial total
        const suppressACMessageForSubscription = isFullyCoveredByAccountCredit && subscriptionNeedsCreditCard;

        const showACMessage =
            (shouldTreatAccountCreditAsFinalPayment || (isStoreCreditApplied && !isPaymentComplete)) && !suppressACMessageForSubscription;

        return {
            shouldTreatAccountCreditAsFinalPayment,
            isCreditCardRequiredError,
            hasAppliedGiftCards: giftCardPaymentGroups.length > 0,
            showACMessage,
            showTopInlineError:
                this.getShouldRenderTopInlinePaymentError({ storeCreditNotEnough, isCreditCardRequiredError }) &&
                !isFullyCoveredByAccountCredit &&
                !showACMessage,
            showSubscriptionError: (isCreditCardRequiredError || suppressACMessageForSubscription) && isSubscriptionOrder,
            suppressACMessageForSubscription
        };
    }

    renderErrorMessages() {
        const { locales, isCreditCardRequiredMessage } = this.props;
        const errorState = this.getErrorMessageState();

        return (
            <>
                {errorState.showACMessage && (
                    <ErrorMsg
                        mt={4}
                        backgroundColor='white'
                        paddingX={1}
                        paddingY={1}
                        color='red'
                        borderRadius={2}
                        display='flex'
                        alignItems='center'
                        fontSize='base'
                        role='alert'
                        aria-live='polite'
                        marginBottom={0}
                    >
                        <span>
                            {errorState.shouldTreatAccountCreditAsFinalPayment
                                ? locales.accountCreditCoversFullOrder
                                : errorState.hasAppliedGiftCards
                                    ? locales.accountCreditAndGCLessThanTotal
                                    : locales.accountCreditLessThanTotal}
                        </span>
                    </ErrorMsg>
                )}

                {errorState.showTopInlineError && (
                    <ErrorMsg
                        mt={4}
                        backgroundColor='nearWhite'
                        paddingX={4}
                        paddingY={3}
                        color={errorState.isCreditCardRequiredError ? 'red' : 'black'}
                        borderRadius={2}
                        display='flex'
                        alignItems='center'
                        fontSize='base'
                        role='alert'
                        aria-live='polite'
                    >
                        <Icon
                            name='alert'
                            color={errorState.isCreditCardRequiredError ? 'red' : 'gray'}
                            size={16}
                            marginRight={2}
                            aria-hidden='true'
                        />
                        <span>{errorState.isCreditCardRequiredError ? isCreditCardRequiredMessage : locales.storeCreditNotEnoughMessage}</span>
                    </ErrorMsg>
                )}

                {errorState.showSubscriptionError && (
                    <ErrorMsg
                        mt={4}
                        backgroundColor='white'
                        paddingX={1}
                        color='red'
                        borderRadius={2}
                        display='flex'
                        alignItems='center'
                        fontSize='base'
                        role='alert'
                        aria-live='polite'
                        marginBottom={0}
                    >
                        <span>
                            {errorState.suppressACMessageForSubscription
                                ? locales.accountCreditSubscriptionMessage
                                : locales.subscriptionCreditCardRequired}
                        </span>
                    </ErrorMsg>
                )}
            </>
        );
    }

    renderSavedAlternatePaymentRadio = payment => {
        const { paymentOptions, locales, installmentValue } = this.props;

        return (
            <SavedAlternatePaymentRadio
                key={payment.type}
                paymentName={payment.paymentName}
                defaultPayment={paymentOptions?.defaultPayment}
                selected={this.state.selectedAlternatePaymentType === payment.type}
                onClick={() => this.handleAlternatePaymentClick(payment.type)}
                onUseThisPaymentMethod={this.handleUseThisPaymentMethod}
                installmentValue={installmentValue}
                username={payment.username}
                isPayPal={payment.paymentName === ALTERNATE_PAYMENT_TYPES.PAYPAL}
                locales={locales}
                isAvailable={this.isPaymentMethodAvailable(payment.type)}
            />
        );
    };

    renderHoistedCreditCardRow = (creditCard, showDivider) => {
        const {
            paymentOptions, locales, isFullyCoveredByAccountCredit, zeroDollarHigherRedeemPointsCVV, isSubscriptionOrder
        } = this.props;
        const creditCardIsDefaultPayment = !paymentOptions?.defaultPayment || paymentOptions.defaultPayment === 'creditCard';
        const showEditRemoveButtons = agentAwareUtils.isAgent ? agentAwareUtils.isAgentAuthorized(['3']) : true;
        // Subscription orders always need a CC for recurring charges, so never
        // disable card selection even when account credit covers the order.
        const shouldDisableCreditCardSelection = isFullyCoveredByAccountCredit && !isSubscriptionOrder;

        return (
            <CreditCardRow
                key={creditCard.creditCardId}
                creditCard={creditCard}
                showDivider={showDivider}
                selectedCreditCardId={this.state.selectedCreditCardId}
                disabled={shouldDisableCreditCardSelection}
                creditCardIsDefaultPayment={creditCardIsDefaultPayment}
                showEditRemoveButtons={showEditRemoveButtons}
                showEditButtonFirst={true}
                shouldDisplayAddOrDeleteCreditCardButton={!zeroDollarHigherRedeemPointsCVV}
                locales={locales}
                onSetNewCreditCard={this.handleSetNewCreditCard}
                onShowEditCreditCardForm={this.handleShowEditCreditCardForm}
                onRemoveCreditCard={this.handleRemoveCreditCard}
                onDeleteCreditCard={this.handleDeleteCreditCard}
                onUseThisCard={this.handleUseThisCardClick}
            />
        );
    };

    renderPaymentFields() {
        const {
            paymentOptions,
            storeCredits,
            shouldDisplayAddOrDeleteCreditCardButton,
            isZeroCheckout,
            isBopis,
            creditCardPaymentGroup,
            zeroDollarHigherRedeemPointsCVV,
            locales,
            isFullyCoveredByAccountCredit,
            isSubscriptionOrder
        } = this.props;

        const { securityCodeInvalid, securityCode } = this.state;
        const allCards = paymentOptions?.creditCards || [];
        const hasSavedCreditCards = allCards.length > 0;
        // Subscription orders always need a CC for recurring charges, so never
        // disable card selection even when account credit covers the order.
        const shouldDisableCreditCardSelection = isFullyCoveredByAccountCredit && !isSubscriptionOrder;
        const showAlts = this.shouldShowSavedAlternatePayment(isFullyCoveredByAccountCredit);

        // Compute priority items (active, default if different). Filter out alts when not allowed.
        const priorityItems = this.getPriorityPaymentItems().filter(item => item.kind !== 'alt' || showAlts);

        const hoistedCcIds = new Set(priorityItems.filter(i => i.kind === 'cc').map(i => i.data.creditCardId));
        const hoistedAltTypes = new Set(priorityItems.filter(i => i.kind === 'alt').map(i => i.data.type));

        const allSavedAlts = showAlts ? this.getSavedAlternatePayments() : [];
        const restAlts = allSavedAlts.filter(p => !hoistedAltTypes.has(p.type));
        const restCards = allCards.filter(c => !hoistedCcIds.has(c.creditCardId));
        const hasMoreAfterPriority = restAlts.length > 0 || restCards.length > 0;
        const hasAvailableSavedAltPayment = allSavedAlts.some(p => this.isPaymentMethodAvailable(p.type));

        return (
            <fieldset>
                <Box
                    is='legend'
                    css={screenReaderOnlyStyle}
                >
                    {locales.paymentMethodSelection}
                </Box>

                {/* 1. Account credit displayed first */}
                {storeCredits?.length > 0 &&
                    storeCredits.map((storeCredit, index) => (
                        <AccountCredit
                            key={`storeCredits_${index.toString()}`}
                            storeCredit={storeCredit}
                            locales={locales}
                            withCheckbox={false}
                            isEditView={true}
                        />
                    ))}

                {/* 2-3. Priority payment items: active, then default (if different from active) */}
                {priorityItems.map((item, i) => {
                    const isLastPriority = i === priorityItems.length - 1;
                    const showDivider = !isLastPriority || hasMoreAfterPriority;

                    return item.kind === 'cc'
                        ? this.renderHoistedCreditCardRow(item.data, showDivider)
                        : this.renderSavedAlternatePaymentRadio(item.data);
                })}

                {/* 4. Rest of saved alternate payments (excluding any already hoisted above) */}
                {restAlts.map(this.renderSavedAlternatePaymentRadio)}

                {/* 5. Rest of credit cards */}
                {restCards.length > 0 && (
                    <CreditCardsList
                        cards={restCards}
                        totalCardsCount={allCards.length}
                        isZeroCheckout={isZeroCheckout}
                        isBopis={isBopis}
                        setNewCreditCard={this.setNewCreditCard}
                        showEditCreditCardForm={this.showEditCreditCardForm}
                        updateSecurityCode={this.updateSecurityCode}
                        shouldDisplayAddOrDeleteCreditCardButton={!zeroDollarHigherRedeemPointsCVV}
                        defaultPayment={paymentOptions?.defaultPayment}
                        securityCode={securityCode}
                        securityCodeInvalid={securityCodeInvalid}
                        selectedCreditCardId={this.state.selectedCreditCardId}
                        creditCardPaymentGroup={creditCardPaymentGroup}
                        creditCardOptions={paymentOptions?.creditCards}
                        showEditButtonFirst={true}
                        onUseThisCard={this.handleUseThisCardClick}
                        onSetNewCreditCard={this.handleSetNewCreditCard}
                        onShowEditCreditCardForm={this.handleShowEditCreditCardForm}
                        onRemoveCreditCard={this.handleRemoveCreditCard}
                        onDeleteCreditCard={this.handleDeleteCreditCard}
                        onRef={comp => (this.creditCardsList = comp)}
                        alternateMethodSelected={this.props.alternateMethodSelected || !!this.state.selectedAlternatePaymentType}
                        hasDefaultAlternatePayment={this.hasSavedAlternatePayment()}
                        giftCardAmountEqualsOrderTotal={this.props.giftCardAmountEqualsOrderTotal}
                        isSubscriptionOrder={this.props.isSubscriptionOrder}
                        disabled={shouldDisableCreditCardSelection}
                    />
                )}

                {shouldDisplayAddOrDeleteCreditCardButton && (
                    <>
                        {hasSavedCreditCards || hasAvailableSavedAltPayment ? (
                            <>
                                {((hoistedAltTypes.size === 0 && restAlts.length === 0) || hasSavedCreditCards) && <Divider marginTop={4} />}
                                <AddCreditCardButton onClick={this.toggleOpenCreditCardForm()} />
                            </>
                        ) : (
                            (!isFullyCoveredByAccountCredit || isSubscriptionOrder) && (
                                <Button
                                    variant='primary'
                                    width={['297px', '297px', '50%']}
                                    marginTop={4}
                                    onClick={this.toggleOpenCreditCardForm()}
                                >
                                    {locales.addNewCard}
                                </Button>
                            )
                        )}
                    </>
                )}

                {/* Save and Continue button when account credit covers full order */}
                {shouldDisableCreditCardSelection && (
                    <Button
                        variant='primary'
                        width={['100%', '100%', '380px']}
                        marginTop={4}
                        onClick={this.saveAndContinueDebounce}
                        data-at={Sephora.debug.dataAt('save_and_continue_account_credit')}
                    >
                        {locales.afterpaySaveAndContinue}
                    </Button>
                )}

                {/*GIFT CARD SECTION: TODO: maybe cab be removed later and used GiftCard from NewUserPaymentView*/}
                {this.renderGiftCard()}

                {/*NEW PAYMENT VIEW SECTION*/}
                {this.renderNewPaymentView()}
            </fieldset>
        );
    }

    render() {
        const { openCreditCardForm, editCreditCard, isEditCCFlow } = this.state;
        const { isGuestCheckout } = this.props;

        const expandedView = this.renderExpandedView();

        if (expandedView) {
            return expandedView;
        }

        return (
            <>
                {/* Hidden container for legacy Klarna background initialization (old modal flow) */}
                {/* This prevents "InvalidContainerSelectorError" when commonOrderToggleActions dispatches backgroundInit */}
                <div
                    id='backgroundPaymentWidget'
                    style={{ display: 'none' }}
                />

                {openCreditCardForm && (
                    <CreditCardArea
                        handleCloseForm={this.toggleOpenCreditCardForm(true)}
                        callback={this.handleCreditCardFormSave}
                        editCreditCard={editCreditCard}
                        isEditCCFlow={isEditCCFlow}
                        isGuestCheckout={isGuestCheckout}
                        hideCancelButton={this.props.openCreditCardForm}
                    />
                )}
                {!openCreditCardForm && (
                    <>
                        {this.renderErrorMessages()}
                        {this.renderPaymentFields()}
                    </>
                )}
            </>
        );
    }
}

export default wrapComponent(PaymentMethodList, 'PaymentMethodList', true);
